/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace chisquare
*/
var chisquare = {};

/**
* @name cdf
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/cdf}
*/
import cdf from './../../../../base/dists/chisquare/cdf';
setReadOnly( chisquare, 'cdf', cdf );

/**
* @name ChiSquare
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/ctor}
*/
import ChiSquare from './../../../../base/dists/chisquare/ctor';
setReadOnly( chisquare, 'ChiSquare', ChiSquare );

/**
* @name entropy
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/entropy}
*/
import entropy from './../../../../base/dists/chisquare/entropy';
setReadOnly( chisquare, 'entropy', entropy );

/**
* @name kurtosis
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/kurtosis}
*/
import kurtosis from './../../../../base/dists/chisquare/kurtosis';
setReadOnly( chisquare, 'kurtosis', kurtosis );

/**
* @name logpdf
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/logpdf}
*/
import logpdf from './../../../../base/dists/chisquare/logpdf';
setReadOnly( chisquare, 'logpdf', logpdf );

/**
* @name mean
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/mean}
*/
import mean from './../../../../base/dists/chisquare/mean';
setReadOnly( chisquare, 'mean', mean );

/**
* @name median
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/median}
*/
import median from './../../../../base/dists/chisquare/median';
setReadOnly( chisquare, 'median', median );

/**
* @name mgf
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/mgf}
*/
import mgf from './../../../../base/dists/chisquare/mgf';
setReadOnly( chisquare, 'mgf', mgf );

/**
* @name mode
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/mode}
*/
import mode from './../../../../base/dists/chisquare/mode';
setReadOnly( chisquare, 'mode', mode );

/**
* @name pdf
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/pdf}
*/
import pdf from './../../../../base/dists/chisquare/pdf';
setReadOnly( chisquare, 'pdf', pdf );

/**
* @name quantile
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/quantile}
*/
import quantile from './../../../../base/dists/chisquare/quantile';
setReadOnly( chisquare, 'quantile', quantile );

/**
* @name skewness
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/skewness}
*/
import skewness from './../../../../base/dists/chisquare/skewness';
setReadOnly( chisquare, 'skewness', skewness );

/**
* @name stdev
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/stdev}
*/
import stdev from './../../../../base/dists/chisquare/stdev';
setReadOnly( chisquare, 'stdev', stdev );

/**
* @name variance
* @memberof chisquare
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/chisquare/variance}
*/
import variance from './../../../../base/dists/chisquare/variance';
setReadOnly( chisquare, 'variance', variance );


// EXPORTS //

export default chisquare;
